import { Link, useNavigate } from "@tanstack/react-router";
import { surfaceBase } from "@web/components/ui";
import { useEffect, useState } from "react";
import { SkeletonBox } from "@/components/skeleton";
import { useToast } from "@/contexts";
import { getCurrentUserProfile } from "@/features/dashboard/api";
import { deleteGroup, listOrgGroups } from "@/features/organizations/groups";
import type { Group, UserProfile } from "@/types";

export default function SettingsGroups() {
  const navigate = useNavigate();
  const toast = useToast();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data, error } = await getCurrentUserProfile();
      if (error) {
        toast.showAlert("Error", error.message, "error");
        setLoading(false);
        return;
      }
      if (!data?.orgId) {
        setLoading(false);
        return;
      }
      setProfile(data);
      const result = await listOrgGroups(data.orgId);
      if (result.error) {
        toast.showAlert("Error", result.error.message, "error");
      } else if (result.data) {
        setGroups(result.data);
      }
      setLoading(false);
    })();
  }, [toast]);

  async function handleDelete(group: Group) {
    toast.showConfirm(
      "Delete group",
      `Delete "${group.name}"? This action cannot be undone.`,
      async () => {
        const { error } = await deleteGroup(group.id);
        if (error) {
          toast.showAlert("Error", error.message, "error");
        } else {
          setGroups((prev) => prev.filter((g) => g.id !== group.id));
          toast.showAlert("Deleted", "Group deleted.", "success");
        }
      },
    );
  }

  const rootGroups = groups.filter((g) => !g.parentId);
  const childGroups = groups.filter((g) => g.parentId);

  function renderGroupCard(group: Group, indent = false) {
    const typeLabel =
      group.type === "office" ? "Office" : group.type === "department" ? "Department" : "Custom";
    const parentName = group.parentId ? groups.find((g) => g.id === group.parentId)?.name : null;

    return (
      <div
        key={group.id}
        className={`${surfaceBase} bg-card p-4 flex flex-col gap-2 ${indent ? "ml-6" : ""}`}
      >
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate({ to: `/settings/groups/${group.id}` })}
            className="flex-1 text-left bg-transparent border-0 cursor-pointer font-inherit"
          >
            <span className="text-text text-base font-extrabold">{group.name}</span>
            <span className="text-text-dim text-[0.75rem] font-bold block">/{group.slug}</span>
          </button>
          <div className="flex items-center gap-2">
            <span
              className={`${surfaceBase} bg-white px-2.5 py-1 text-[0.625rem] font-bold uppercase tracking-[0.05em]`}
            >
              {typeLabel}
            </span>
            <span className="text-text-dim text-[0.75rem] font-bold">
              {group.memberCount} members
            </span>
          </div>
        </div>
        {parentName && <span className="text-text-dim text-[0.75rem]">Parent: {parentName}</span>}
        <div className="flex gap-2 mt-1">
          <button
            type="button"
            onClick={() => navigate({ to: `/settings/groups/${group.id}` })}
            className="flex-1 bg-aqua rounded-[12px] py-2 px-4 text-white text-[0.75rem] font-extrabold border border-text cursor-pointer"
          >
            Manage
          </button>
          <button
            type="button"
            onClick={() => handleDelete(group)}
            className="bg-white rounded-[12px] py-2 px-4 text-coral text-[0.75rem] font-extrabold border border-text cursor-pointer"
          >
            Delete
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background relative">
        <div className="h-screen overflow-y-auto">
          <div className="flex flex-col px-5 pt-3 pb-9 gap-5 md:px-5 max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
              <Link
                to="/settings"
                className="py-1.5 px-2.5 border border-[rgba(28,27,46,0.1)] rounded-[16px] bg-[rgba(255,255,255,0.4)] no-underline"
              >
                <span className="text-text-muted text-[0.8125rem] font-bold">&larr; Back</span>
              </Link>
              <span className="text-text text-[1.75rem] font-black tracking-[-0.0313em]">
                Groups
              </span>
            </div>
            <div className="flex flex-col gap-4">
              <SkeletonBox width="100%" height={120} borderRadius={20} />
              <SkeletonBox width="100%" height={120} borderRadius={20} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile?.orgId) {
    return (
      <div className="min-h-screen bg-background relative">
        <div className="h-screen overflow-y-auto">
          <div className="flex flex-col px-5 pt-3 pb-9 gap-5 md:px-5 max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
              <Link
                to="/settings"
                className="py-1.5 px-2.5 border border-[rgba(28,27,46,0.1)] rounded-[16px] bg-[rgba(255,255,255,0.4)] no-underline"
              >
                <span className="text-text-muted text-[0.8125rem] font-bold">&larr; Back</span>
              </Link>
              <span className="text-text text-[1.75rem] font-black tracking-[-0.0313em]">
                Groups
              </span>
            </div>
            <div className={`${surfaceBase} bg-card p-5 text-center`}>
              <span className="text-text-dim text-[1rem]">
                You are not part of an organization.
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      <div className="h-screen overflow-y-auto">
        <div className="flex flex-col px-5 pt-3 pb-9 gap-5 md:px-5 max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <Link
              to="/settings"
              className="py-1.5 px-2.5 border border-[rgba(28,27,46,0.1)] rounded-[16px] bg-[rgba(255,255,255,0.4)] no-underline"
            >
              <span className="text-text-muted text-[0.8125rem] font-bold">&larr; Back</span>
            </Link>
            <span className="text-text text-[1.75rem] font-black tracking-[-0.0313em]">Groups</span>
          </div>

          <button
            type="button"
            onClick={() => navigate({ to: "/settings/groups/new" })}
            className="bg-aqua rounded-[16px] py-3 px-5 text-white text-[0.875rem] font-extrabold border border-text self-start cursor-pointer"
          >
            Create group
          </button>

          {groups.length === 0 && (
            <div className={`${surfaceBase} bg-card p-5 text-center`}>
              <span className="text-text-dim text-[1rem]">
                No groups yet. Create your first group to organize your org.
              </span>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <span className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]">
              Top-level groups
            </span>
            {rootGroups.map((g) => renderGroupCard(g))}
          </div>

          {childGroups.length > 0 && (
            <div className="flex flex-col gap-3">
              <span className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]">
                Child groups
              </span>
              {childGroups.map((g) => renderGroupCard(g, true))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
